<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import ChevronDown from '/svg/chevron-down.svg'
import { isoToFaName } from '../constants/iso-to-fa-name.constant'

interface CountryOption {
  label: string
  value: string
  flagUrl: string
  iso: string
}

interface Props {
  modelValue: string | number
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  searchable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  searchable: true,
  placeholder: '',
})

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const { countries } = useGeneralSettings()

const isFocused = ref(false)
const isOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)
const searchQuery = ref('')
const selectedOption = ref<CountryOption | undefined>()

onClickOutside(wrapper, () => {
  isOpen.value = false
  searchQuery.value = ''
})

const getFaName = (country: CountryOption): string => {
  return isoToFaName[country.iso] || country.label || 'نامشخص'
}

const filteredOptions = computed(() => {
  if (!searchQuery.value || !props.searchable) return countries.value

  return countries.value.filter(
    (country) =>
      getFaName(country).includes(searchQuery.value) ||
      country.label?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      country.iso?.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

function updateValue(value: string | number) {
  emit('update:modelValue', value)
  selectedOption.value = countries.value.find((country) => country.value === value)
  isOpen.value = false
  searchQuery.value = ''
}

function toggleOpen() {
  if (!props.disabled) {
    isOpen.value = !isOpen.value
    if (isOpen.value && props.searchable) {
      setTimeout(() => {
        const searchInput = document.getElementById('search-input')
        if (searchInput) searchInput.focus()
      }, 100)
    } else {
      searchQuery.value = ''
    }
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    selectedOption.value = countries.value.find((country) => country.value === newValue)
  }
)

watch(
  () => countries.value,
  () => {
    selectedOption.value = countries.value.find((country) => country.value === props.modelValue)
  },
  { immediate: true }
)
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full h-[71.47px]" ref="wrapper">
        <!-- Label -->
        <label v-if="label"
            class="w-fit bg-white px-1 transition-all z-30 duration-400 text-[#6C6C6C] pointer-events-none" :class="(isFocused || modelValue)
                ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
                : 'absolute mt-[13px] ms-5 text-[13px] font-medium'">
            {{ label }}
        </label>

        <!-- Custom Select Wrapper -->
        <div class="relative w-full h-[48px]">
            <!-- Selected Value Display -->
            <div class="cursor-pointer w-full h-full rounded-xl border px-[18px] pr-8 text-[13px] font-medium outline-none transition
                       border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-[#000]
                       disabled:bg-gray-100 disabled:cursor-not-allowed bg-white flex items-center"
                :class="error ? 'border-[#ed145b]' : ''" @click="toggleOpen">
                <span class="text-[#1A1A1A] flex items-center gap-2">
                    <template v-if="selectedOption">
                        {{ getFaName(selectedOption) }}
                    </template>
                    <template v-else>
                        {{ placeholder }}
                    </template>
                </span>
            </div>

            <!-- Chevron Icon -->
            <img :src="ChevronDown" alt="chevron"
                class="absolute top-1/2 end-0.5 -translate-y-1/2 w-6 h-6 pointer-events-none transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }" />

            <!-- Custom Dropdown Options -->
            <div v-if="isOpen"
                class="absolute top-full left-0 right-0 mt-1 bg-white border border-[#d5ddea] rounded-xl shadow-lg z-[999] max-h-60 overflow-y-auto custom-dropdown">

                <!-- Search Input (only when searchable is true) -->
                <div v-if="searchable" class="sticky top-0 bg-white p-2 border-b border-[#d5ddea]">
                    <input id="search-input" type="text" v-model="searchQuery" placeholder="نام کشور را جستجو کنید..."
                        class="w-full px-3 py-2 text-sm border border-[#d5ddea] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1b325a]"
                        @keydown.esc="isOpen = false; searchQuery = ''" />
                </div>

                <!-- Options List -->
                <div v-if="filteredOptions.length > 0">
                    <div v-for="country in filteredOptions" :key="country.value"
                        class="px-4 py-3 cursor-pointer transition-all duration-200 text-[15px] font-medium flex items-center gap-3"
                        :class="[
                            country.value === modelValue
                                ? 'bg-[#DAE9F8] text-[#1b325a] font-bold'
                                : 'text-[#1b325a] hover:bg-[#f8f9fa]',
                            error ? 'border-r-4 border-r-[#ed145b]' : ''
                        ]" @click="updateValue(country.value)">
                        <img v-if="country.flagUrl" :src="country.flagUrl" :alt="getFaName(country)"
                            class="w-6 h-4 object-cover rounded flex-shrink-0" />
                        <span class="flex-1">{{ getFaName(country) }}</span>
                    </div>
                </div>
                <div v-else class="px-4 py-3 text-[15px] text-gray-500 text-center">
                    کشوری یافت نشد
                </div>
            </div>
        </div>

        <!-- Hidden Native Select for Form Submission -->
        <select :disabled="disabled" :value="modelValue"
            @change="(e) => updateValue((e.target as HTMLSelectElement).value)" class="hidden">
            <option v-for="country in countries" :key="country.value" :value="country.value">{{ getFaName(country) }}
            </option>
        </select>

        <!-- Error -->
        <div v-if="error" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error-icon" />{{ error }}
        </div>
    </div>
</template>

<style scoped>
/* Custom scrollbar for dropdown */
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>